import { useEffect } from "react";
import { installFullscreenAutoRestore, maybeRestoreFullscreen } from "./local/fullscreen";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { MenuBackdrop } from "./components/MenuBackdrop";
import { MenuMusic } from "./components/MenuMusic";
import { GamepadNavigation } from "./components/GamepadNavigation";
import { LocalLanding } from "./pages/LocalLanding";
import { LocalCreate } from "./pages/LocalCreate";
import { LocalLogin } from "./pages/LocalLogin";
import { LocalPlay } from "./pages/LocalPlay";
import { PackLevelPicker } from "./pages/PackLevelPicker";
import { LocalSettings } from "./pages/LocalSettings";
import { LocalCharacter } from "./pages/LocalCharacter";
import { Flashcards } from "./pages/Flashcards";
import { LessonPlay } from "./pages/LessonPlay";
import { RaceLobby } from "./pages/RaceLobby";
import { RacePlay } from "./pages/RacePlay";
import { RaceResults } from "./pages/RaceResults";
import { LocalTeacher, LocalTeacherLessonEditor } from "./pages/LocalTeacher";
import { SeasonRoad } from "./pages/SeasonRoad";
import { CustomPack } from "./pages/CustomPack";
import { Printables } from "./pages/Printables";
import { QuizzleEntry } from "./pages/QuizzleEntry";
import { QuizzleHost } from "./pages/QuizzleHost";
import { QuizzleController } from "./pages/QuizzleController";
import { DrawEntry } from "./pages/DrawEntry";
import { DrawHost } from "./pages/DrawHost";
import { DrawController } from "./pages/DrawController";
import { ClassEntry } from "./pages/ClassEntry";
import { ClassLogin } from "./pages/ClassLogin";
import { ClassLobby } from "./pages/ClassLobby";
import { ClassHost } from "./pages/ClassHost";

/**
 * Game routing — standard menu shape.
 *
 *   /                  → main menu (Play / Battle / Settings)
 *   /create            → first-time character setup
 *   /play              → single-player level select
 *   /play/lesson/:id   → in-game (single player)
 *   /battle            → multiplayer lobby
 *   /battle/play       → in-game (multiplayer)
 *   /battle/results    → multiplayer podium
 *   /settings          → name / avatar / reset / about
 */
export function App() {
  useEffect(() => {
    // Arm fullscreen auto-restore once at boot. We capture whether the app
    // was fullscreen at the moment an <input> got focus (e.g. typing a
    // multiplayer code), so any subsequent button press in the lobby can
    // re-request fullscreen with a valid user-gesture context.
    installFullscreenAutoRestore();
    const onPointerUp = () => {
      maybeRestoreFullscreen();
      // Try to lock orientation to landscape on each user gesture. The
      // browser only grants this in fullscreen + secure-context, so a
      // tap that triggers fullscreen also unlocks orientation lock.
      const so = (screen as Screen & { orientation?: { lock?: (o: string) => Promise<void> } }).orientation;
      so?.lock?.("landscape").catch(() => { /* unsupported / not granted */ });
    };
    document.addEventListener("pointerup", onPointerUp, true);
    return () => document.removeEventListener("pointerup", onPointerUp, true);
  }, []);
  const location = useLocation();
  const inGame = /^\/(play\/lesson|battle\/play|quizzle\/(host|play)|drawit\/(host|play))/.test(location.pathname);
  return (
    <>
      <MenuMusic active={!inGame} />
      <GamepadNavigation />
      {!inGame && <MenuBackdrop />}
      <div className="page-content">
        <Routes>
          <Route path="/" element={<LocalLanding />} />
          <Route path="/create" element={<LocalCreate />} />
          <Route path="/login" element={<LocalLogin />} />
          <Route path="/play" element={<PackLevelPicker />} />
          <Route path="/season" element={<SeasonRoad />} />
          <Route path="/custom-pack" element={<CustomPack />} />
          <Route path="/play/legacy" element={<LocalPlay />} />
          <Route path="/play/lesson/:lessonId" element={<LessonPlay />} />
          <Route path="/play/pack/:packId/level/:levelN" element={<LessonPlay />} />
          <Route path="/battle" element={<RaceLobby />} />
          <Route path="/battle/play" element={<RacePlay />} />
          <Route path="/battle/results" element={<RaceResults />} />
          <Route path="/settings" element={<LocalSettings />} />
          <Route path="/character" element={<LocalCharacter />} />
          <Route path="/flashcards" element={<Flashcards />} />
          <Route path="/flashcards/:packId" element={<Flashcards />} />
          <Route path="/teacher" element={<LocalTeacher />} />
          <Route path="/teacher/:lessonId" element={<LocalTeacherLessonEditor />} />
          <Route path="/printables" element={<Printables />} />
          <Route path="/quizzle" element={<QuizzleEntry />} />
          <Route path="/quizzle/host" element={<QuizzleHost />} />
          <Route path="/quizzle/play" element={<QuizzleController />} />
          <Route path="/drawit" element={<DrawEntry />} />
          <Route path="/drawit/host" element={<DrawHost />} />
          <Route path="/drawit/play" element={<DrawController />} />
          <Route path="/class" element={<ClassEntry />} />
          <Route path="/class/login" element={<ClassLogin />} />
          <Route path="/class/lobby" element={<ClassLobby />} />
          <Route path="/class/host" element={<ClassHost />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </>
  );
}
